import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  fetchKpisThunk,
  fetchChartDataThunk,
} from "../../../store/thunks/analyticsThunks";
import { fetchChannelStatusThunk } from "../../../store/thunks/campaignThunks";
import StatCard from "../../../components/charts/StatCard";
import Button from "../../../components/common/Button";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import {
  PaperAirplaneIcon,
  CheckCircleIcon,
  CursorClickIcon,
  ChartBarIcon,
  PlusIcon,
  DeviceMobileIcon,
} from "@heroicons/react/outline";
import {
  formatNumber,
  formatPercentage,
} from "../../../services/analyticsService";
import { chartColors } from "../constants/notificationConstants";
import { NOTIFICATION_ROUTES } from "../../../constants/notification.constant";

const NotificationsDashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { kpis, chartData, loading } = useSelector(
    (state) => state.notificationAnalytics
  );
  const { list: templates } = useSelector(
    (state) => state.notificationTemplates
  );
  const { jobs } = useSelector((state) => state.notificationCampaigns);
  const { rows: devices } = useSelector((state) => state.notificationDevices);
  const { status: channelStatus } = useSelector(
    (state) => state.notificationCampaigns.channels
  );

  // Helper to count jobs created today
  const jobsTodayCount = jobs
    ? jobs.filter((job) => {
        const createdAt = job.createdAt || job.created_at;
        if (!createdAt) return false;
        const jobDate = new Date(createdAt).toDateString();
        const today = new Date().toDateString();
        return jobDate === today;
      }).length
    : 0;

  // Helper to count active devices (matching backend isActive field)
  const activeDevicesCount = devices
    ? devices.filter((device) => device.isActive).length
    : 0;

  // Helper to count anonymous devices (matching backend isAnonymous field)
  const anonymousDevicesCount = devices
    ? devices.filter((device) => device.isAnonymous).length
    : 0;

  useEffect(() => {
    // Fetch KPIs
    dispatch(fetchKpisThunk());

    // Fetch top templates chart data
    dispatch(
      fetchChartDataThunk({
        chartType: "topTemplates",
        params: { groupBy: "template", range: "30d" },
      })
    );

    // Fetch channel status
    dispatch(fetchChannelStatusThunk());
  }, [dispatch]);

  const handleCreateCampaign = () => {
    navigate(NOTIFICATION_ROUTES.CAMPAIGNS);
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white dark:bg-gray-800 p-3 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700">
          <p className="font-semibold">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {formatNumber(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Channel status display helper
  const renderChannelStatus = () => {
    return (
      <div className="mt-4">
        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Channel Status
        </h3>
        <div className="grid grid-cols-2 gap-2">
          {Object.entries(channelStatus).map(([channel, enabled]) => (
            <div
              key={channel}
              className="flex justify-between items-center p-2 bg-gray-50 dark:bg-gray-900 rounded"
            >
              <span className="text-gray-700 dark:text-gray-300">
                {channel === "FCM" && "Android"}
                {channel === "APNS" && "iOS"}
                {channel === "WEB" && "Web"}
                {channel === "WHATSAPP" && "WhatsApp"}
              </span>
              <span
                className={`text-sm font-medium px-2 py-1 rounded-full ${
                  enabled
                    ? "bg-green-100 text-green-800"
                    : "bg-gray-100 text-gray-600"
                }`}
              >
                {enabled ? "Enabled" : "Disabled"}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="container mx-auto p-4">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Push Notifications Dashboard
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Monitor your notification performance and engagement metrics
          </p>
        </div>
        <Button
          variant="primary"
          onClick={handleCreateCampaign}
          className="flex items-center gap-2"
        >
          <PlusIcon className="w-5 h-5" />
          Create Campaign
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <StatCard
          title="Sent Today"
          value={kpis.sentToday}
          icon={PaperAirplaneIcon}
          color="primary"
          loading={loading}
        />
        <StatCard
          title="Total Sent"
          value={kpis.totalSent}
          icon={ChartBarIcon}
          color="info"
          loading={loading}
        />
        <StatCard
          title="Delivery Rate"
          value={kpis.deliveryRate}
          format="percentage"
          icon={CheckCircleIcon}
          color="success"
          loading={loading}
        />
        <StatCard
          title="Open Rate"
          value={kpis.openRate}
          format="percentage"
          icon={ChartBarIcon}
          color="info"
          loading={loading}
        />
        <StatCard
          title="Click Rate"
          value={kpis.clickRate}
          format="percentage"
          icon={CursorClickIcon}
          color="warning"
          loading={loading}
        />
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Templates Chart */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
            Top Templates (Last 30 Days)
          </h2>
          {loading ? (
            <div className="h-80 bg-gray-100 dark:bg-gray-700 rounded-lg animate-pulse" />
          ) : (
            <ResponsiveContainer width="100%" height={320}>
              <BarChart
                data={
                  Array.isArray(chartData.topTemplates)
                    ? chartData.topTemplates.slice(0, 10)
                    : []
                }
                margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="name"
                  angle={-45}
                  textAnchor="end"
                  height={100}
                  tick={{ fontSize: 12 }}
                />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Bar
                  dataKey="sent"
                  fill={chartColors.primary}
                  name="Sent"
                  radius={[8, 8, 0, 0]}
                />
                <Bar
                  dataKey="delivered"
                  fill={chartColors.info}
                  name="Delivered"
                  radius={[8, 8, 0, 0]}
                />
                <Bar
                  dataKey="opened"
                  fill={chartColors.success}
                  name="Opened"
                  radius={[8, 8, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Quick Actions */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
            Quick Actions
          </h2>
          <div className="space-y-3">
            <Button
              variant="outline"
              fullWidth
              onClick={() => navigate(NOTIFICATION_ROUTES.TEMPLATES)}
              className="justify-start"
            >
              Manage Templates
            </Button>
            <Button
              variant="outline"
              fullWidth
              onClick={() => navigate(NOTIFICATION_ROUTES.JOBS)}
              className="justify-start"
            >
              View Recent Jobs
            </Button>
            <Button
              variant="outline"
              fullWidth
              onClick={() => navigate(NOTIFICATION_ROUTES.DEVICES)}
              className="justify-start"
            >
              Device Registry
            </Button>
            <Button
              variant="outline"
              fullWidth
              onClick={() => navigate(NOTIFICATION_ROUTES.ANALYTICS)}
              className="justify-start"
            >
              Detailed Analytics
            </Button>
          </div>

          {/* Recent Activity Summary */}
          <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Recent Activity
            </h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">
                  Active Templates
                </span>
                <span className="font-medium">{templates.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">
                  Jobs Today
                </span>
                <span className="font-medium">{jobsTodayCount}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">
                  Active Devices
                </span>
                <span className="font-medium">{devices.length}</span>
              </div>
            </div>
          </div>

          {/* Channel Status */}
          {Object.keys(channelStatus).length > 0 && renderChannelStatus()}
        </div>
      </div>
    </div>
  );
};

export default NotificationsDashboard;
